import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  DialogActions,
  Typography,
  Modal as MuiModal,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Checkbox,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useApiClient } from '../../context/ApiClientContext';
import { axiosErrorToUserMessage } from '../../services/axios';
import useSitePermissionUpdate from '../../hooks/useSitePermissionUpdate';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '1px solid #ccc',
  borderRadius: '8px',
  p: 4,
};

function AddSitesModalButton(props) {
  const {
    user,
    fetchUsers,
    isOpen,
    onOpen,
    onClose,
    setSuccessMessage,
    setErrorMessage,
    allSites,
  } = props;

  const [selectedSites, setSelectedSites] = useState([]);
  const apiClient = useApiClient();
  const { handleSiteAssignment, getCurrentUsername } = useSitePermissionUpdate();

  const unassignedSites = useMemo(() => {
    if (!isOpen || !allSites.length) return [];

    // If user has access to all sites (*), there are no unassigned sites
    if (user.siteIds.includes('*')) {
      return [];
    }

    // Filter out sites that the user already has access to
    return allSites.filter((site) => !user.siteIds.includes(site.id));
  }, [isOpen, allSites, user.siteIds]);

  // Initialize selected sites when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSites([]);
    }
  }, [isOpen]);

  const handleSiteCheckboxChange = (siteId) => {
    setSelectedSites((prevSelectedSites) => {
      if (prevSelectedSites.includes(siteId)) {
        return prevSelectedSites.filter((id) => id !== siteId);
      }
      return [...prevSelectedSites, siteId];
    });
  };

  const handleAssignSites = async () => {
    if (selectedSites.length === 0) {
      setErrorMessage('Please select at least one site to assign.');
      return;
    }

    try {
      const currentSiteIds = user.siteIds.includes('*') ? [] : user.siteIds;
      const newSiteIds = [...currentSiteIds, ...selectedSites];

      const response = await apiClient.assignSitesToUser(user.username, newSiteIds);
      if (response.status === 200) {
        // If current user is assigning sites to themselves,
        // update local accessible sites list and force token refresh to sync with db
        const currentUsername = getCurrentUsername();
        if (currentUsername && currentUsername === user.username) {
          await handleSiteAssignment(selectedSites);
        }
        setSuccessMessage(`Successfully assigned ${selectedSites.length} site(s) to ${user.username}.`);
        // Wrap fetchUsers to handle errors gracefully
        try {
          await fetchUsers();
        } catch {
          setErrorMessage('Sites assigned successfully, but failed to refresh the user list. Please refresh the page.');
        }
        onClose();
      }
    } catch (error) {
      setErrorMessage(
        axiosErrorToUserMessage(error) || 'Something went wrong while assigning sites.',
      );
    }
  };

  const isAllSelected = unassignedSites.length > 0
    && selectedSites.length === unassignedSites.length;
  const isIndeterminate = selectedSites.length > 0 && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedSites([]);
    } else {
      setSelectedSites(unassignedSites.map((site) => site.id));
    }
  };

  const handleCloseModal = () => {
    onClose();
  };

  let modalBodyContent;

  if (user.siteIds.includes('*') || unassignedSites.length === 0) {
    modalBodyContent = (
      <Typography variant="body1" sx={{ textAlign: 'center', mt: 2 }}>
        This user has access to all sites. No additional sites can be assigned.
      </Typography>
    );
  } else {
    modalBodyContent = (
      <List>
        <ListItemButton
          onClick={handleSelectAll}
          dense
        >
          <ListItemIcon>
            <Checkbox
              edge="start"
              checked={isAllSelected}
              indeterminate={isIndeterminate}
            />
          </ListItemIcon>
          <ListItemText primary="Select All" />
        </ListItemButton>
        {unassignedSites.map((site) => (
          <ListItemButton
            key={site.id}
            onClick={() => handleSiteCheckboxChange(site.id)}
            dense
          >
            <ListItemIcon>
              <Checkbox
                edge="start"
                checked={selectedSites.includes(site.id)}
              />
            </ListItemIcon>
            <ListItemText id={site.id} primary={site.name} />
          </ListItemButton>
        ))}
      </List>
    );
  }

  return (
    <>
      <Tooltip title="Add Sites">
        <Button
          variant="text"
          size="medium"
          onClick={onOpen}
        >
          <AddRoundedIcon sx={{ fontSize: '48' }} />
        </Button>
      </Tooltip>
      <MuiModal
        open={isOpen}
        onClose={handleCloseModal}
        aria-labelledby="assign-sites-modal"
        aria-describedby="assign-sites-modal"
        data-testid="assign-sites-modal"
      >
        <Box sx={modalStyle}>
          <Typography
            id="assign-sites-modal"
            variant="h6"
            component="h2"
            sx={{ marginBottom: '16px' }}
          >
            {user.username}
          </Typography>
          {user.siteIds.includes('*') && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                marginBottom: '16px',
                padding: '8px 12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
              }}
            >
              This user has access to all sites. No additional sites can be assigned.
            </Typography>
          )}
          <Box
            sx={{
              border: '1px solid #ccc',
              borderRadius: '8px',
              padding: '10px',
              height: '500px',
              overflow: 'auto',
            }}
          >
            {modalBodyContent}
          </Box>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button
              onClick={handleAssignSites}
              variant="contained"
              color="primary"
              disabled={user.siteIds.includes('*') || unassignedSites.length === 0 || selectedSites.length === 0}
            >
              Assign
            </Button>
          </DialogActions>
        </Box>
      </MuiModal>
    </>
  );
}

AddSitesModalButton.propTypes = {
  user: PropTypes.shape({
    username: PropTypes.string.isRequired,
    siteIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  fetchUsers: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  allSites: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  setSuccessMessage: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
};

AddSitesModalButton.defaultProps = {
  allSites: [],
};

export default AddSitesModalButton;
